import { QueryKey, useQuery, UseQueryOptions } from 'react-query';
import { fakeApi } from '@frontend/demo-interceptors';
import { LimitedSchemaQueryOptions, SchemaQueryKey } from '@frontend/react-query-helpers';
import type { SchemaIO } from '@frontend/schema';
import { defaultOptions } from '../../config';
import { ServiceTaskStandardQueries, StandardTaskQueryEndpointName } from '../../types';
import { useTaskQuery } from '../use-task-query';

interface RealQueryArgs<EndpointName extends StandardTaskQueryEndpointName, E, T> {
  endpointName: EndpointName;
  request: SchemaIO<ServiceTaskStandardQueries[EndpointName]>['input'];
  options?: LimitedSchemaQueryOptions<
    SchemaIO<ServiceTaskStandardQueries[EndpointName]>,
    E,
    T,
    SchemaQueryKey<ServiceTaskStandardQueries, EndpointName>
  >;
}

export type DemoData<T> = T | (() => T | Promise<T>);

export interface UseTasksAdaptiveQueryArgs<
  EndpointName extends StandardTaskQueryEndpointName,
  E = unknown,
  T = SchemaIO<ServiceTaskStandardQueries[EndpointName]>['output']
> {
  buildDemoQueryKey?: (args: {
    endpointName: EndpointName;
    request: SchemaIO<ServiceTaskStandardQueries[EndpointName]>['input'];
  }) => QueryKey;
  demoData?: DemoData<T>;
  demoDelayMs?: number;
  demoQueryEnabled?: boolean;
  demoQueryOptions?: Omit<UseQueryOptions<T, E, T>, 'queryKey' | 'queryFn'>;
  isDemoAccount?: boolean;
  realQueryArgs: RealQueryArgs<EndpointName, E, T>;
  realQueryEnabled?: boolean;
}

export function useTasksAdaptiveQuery<
  EndpointName extends StandardTaskQueryEndpointName,
  E = unknown,
  T = SchemaIO<ServiceTaskStandardQueries[EndpointName]>['output']
>({
  isDemoAccount,
  realQueryArgs,
  realQueryEnabled = true,
  demoData,
  demoQueryOptions,
  demoQueryEnabled = true,
  demoDelayMs = 200,
  buildDemoQueryKey,
}: UseTasksAdaptiveQueryArgs<EndpointName, E, T>) {
  const { endpointName, request, options: realOpts } = realQueryArgs;

  const defaultKey = ['demo', endpointName, request] as const;
  const demoQueryKey = buildDemoQueryKey ? buildDemoQueryKey({ endpointName, request }) : defaultKey;

  const demoQuery = useQuery<T, E>({
    queryKey: demoQueryKey,
    queryFn: async () => {
      const val = typeof demoData === 'function' ? await (demoData as () => T | Promise<T>)() : (demoData as T);
      return fakeApi(val, demoDelayMs);
    },
    enabled: isDemoAccount && demoQueryEnabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    ...defaultOptions,
    ...demoQueryOptions,
  });

  const realEnabled = !isDemoAccount && realQueryEnabled && (realOpts?.enabled ?? true);
  const realQuery = useTaskQuery<EndpointName, E, T>({
    endpointName,
    request,
    options: {
      enabled: realEnabled,
      ...realOpts,
    },
  });

  return isDemoAccount ? { ...demoQuery, queryKey: demoQueryKey } : realQuery;
}
